type Label = {
  val: string;
  label: string;
};

export interface AddressMetadata {
  recentActivityDate: string;
  recentActivityUpdatedOn: string;
  recentActivityAddedBy: string;
  recentActivityAddedByEmail: string;
  recentActivityAddedByName: string;
  addedByName: string;
  addedByEmail: string;
  proofOfSourceUrl: string;
}
export interface Address {
  referenceId: string;
  addressText: string;
  pinCode: string;
  city: string;
  source: string;
  capturedAt: number;
  rank: number;
  entityId: string;
  state: string;
  latitude: number;
  longitude: number;
  visited: boolean;
  similarLocationEntities: SimilarLocation[];
  labels: Label[];
  locationSubType: string;
  relatedLocationIds: string[];
  latestFeedback: LatestFeedback;
  secondarySource: string;
  metadata: AddressMetadata;
}

export interface LatestFeedback {
  addressReferenceId: string;
  latestFeedbackStatus: string;
  latestFeedbackStatusTag: string;
  latestFeedbackTimestamp: number;
  feedbackColourCode: string;
  feedbackBgColourCode: string;
  isSuspicious: boolean;
  promiseToPayDate: string;
  feedbackPresent: boolean;
}
export interface SimilarLocation {
  referenceId: string;
  addressText: string;
  pinCode: string;
  city: string;
  source: string;
  capturedAt: number;
  rank: number;
  secondarySource: string;
  metadata?: AddressMetadata;
}

export interface TopAddressesData {
  unifiedLocations: Address[];
}

export interface AddressCardProps {
  address: Address;
}

export interface AddressCardFeedbackProps {
  latestFeedback: LatestFeedback;
  addressLine: string;
  relatedLocationIds: string[];
}

export interface AddressCardHeaderProps {
  address: Address;
}

export interface SimilarAddressDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  address: Address;
  onViewProof?: (source: string, proofOfSourceUrl: string) => void;
}

export enum LocationSubType {
  SKIP_TRACE_ADDRESS = 'SKIP_TRACE_ADDRESS',
  GEO_LOCATION = 'GEO_LOCATION',
  META_ADDRESS = 'META_ADDRESS'
}
export enum PrimarySourcesType {
  LONGHORN_CIU = 'LONGHORN_CIU',
  LONGHORN = 'LONGHORN',
  CIBIL_SCRUB = 'CIBIL_SCRUB',
  GST = 'GST',
  BANK_STATEMENT = 'BANK_STATEMENT',
  MMI = 'MMI',
  CIBIL = 'CIBIL',
  CKYC = 'CKYC',
  FINORAMIC = 'FINORAMIC',
  CUSTOMER = 'CUSTOMER',
  CIBIL_TRIGGER = 'CIBIL_TRIGGER',
  AADHAAR = 'AADHAAR',
  AVS_V1 = 'AVS_V1',
  KARZA = 'KARZA',
  'Field App' = 'Field App',
  SKIP_TRACING = 'SKIP_TRACING',
  ACCOUNT_AGGREGATOR = 'ACCOUNT_AGGREGATOR'
}

export const PRIMARY_SOURCE_MAPPING = {
  [PrimarySourcesType.LONGHORN_CIU]: 'Longhorn',
  [PrimarySourcesType.LONGHORN]: 'Longhorn',
  [PrimarySourcesType.CIBIL_SCRUB]: 'CIBIL',
  [PrimarySourcesType.GST]: 'GST',
  [PrimarySourcesType.BANK_STATEMENT]: 'Bank Statement',
  [PrimarySourcesType.MMI]: 'MMI',
  [PrimarySourcesType.CIBIL]: 'CIBIL',
  [PrimarySourcesType.CKYC]: 'CKYC',
  [PrimarySourcesType.FINORAMIC]: 'Finoramic',
  [PrimarySourcesType.CUSTOMER]: 'Customer',
  [PrimarySourcesType.CIBIL_TRIGGER]: 'CIBIL',
  [PrimarySourcesType.AADHAAR]: 'Aadhaar',
  [PrimarySourcesType.AVS_V1]: 'AVS_V1',
  [PrimarySourcesType.KARZA]: 'Karza',
  [PrimarySourcesType['Field App']]: 'COSMOS',
  [PrimarySourcesType.SKIP_TRACING]: 'Customer outreach',
  [PrimarySourcesType.ACCOUNT_AGGREGATOR]: 'Account Aggregator'
};

export enum AnswerType {
  TEXT = 'TEXT',
  DATE = 'DATE',
  TIME = 'TIME',
  AMOUNT = 'AMOUNT',
  OPTIONS = 'OPTIONS',
  REMINDER = 'REMINDER',
  PHONE_NUMBER = 'PHONE_NUMBER'
}
export enum OPTION_TAG {
  COMMENTS = 'COMMENTS',
  IMAGE_UPLOAD = 'IMAGE_UPLOAD'
}

export interface IViewAllFeedbacksDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  addressLine: string;
  relatedLocationIds: string[];
}
export interface IFeedbackDetailsListProps {
  addressText: string;
  feedbackList: IFeedback[];
  showLoader: boolean;
}

export interface IPastFeedbacksOnAddressesPayload {
  loanAccountNumber: string;
  addressReferenceIds: string;
}

export interface IImageMetadata {
  image_created_at: string;
  originalDocumentSignedUri: string;
}

export interface IFeedbackDetailImageItem {
  image: IAnswerView;
}

export interface IGeolocation {
  latitude: number;
  longitude: number;
  type: string;
  primarySource: string;
  secondarySource: string;
  customer_id: string;
  tag: string;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
  metadata: string;
  id: string;
  visitType: string;
  sourceType: string;
  source?: string;
  pingCount: number;
  clusterLabels: string[];
  clusterId: string;
  minTimestamp: string;
  addressString?: string;
}

export interface IAnswerView {
  interactionId?: number;
  referenceId?: string;
  questionReferenceId?: string;
  questionName?: string;
  questionText?: string;
  questionKey?: string;
  answerType?: AnswerType;
  optionReferenceId?: string;
  optionText?: string;
  inputAmount?: number;
  inputDate?: string;
  inputText?: string;
  questionTag?: OPTION_TAG;
  metadata?: IImageMetadata;
}

export interface FIELD_FEEDBACK_METADATA {
  interactionLatitude: string;
  interactionLongitude: string;
  userDistanceFromAddress: string;
}

export interface ICallingFeedback {
  recipientName: string;
  recipientNumber: string;
  sourceType: string;
}

export interface IFeedback {
  referenceId: string;
  type: string;
  accountNumber: string;
  createdAt: string;
  metadata: FIELD_FEEDBACK_METADATA | any;
  sourceType: string;
  sourceText: string;
  answerViews: IAnswerView[];
  imageUrls: string[];
  agentReferenceId: string;
  source: Address | IGeolocation | ICallingFeedback;
  interactionStatus: string;
  tagTitle?: string;
  offset?: number;
  color?: string;
  agentName?: string;
  agentNumber?: string;
}

export const ADDRESS_FILTER_SOURCES = {
  LONGHORN_CIU: 'LONGHORN_CIU',
  CUSTOMER_OUTREACH: 'CUSTOMER_OUTREACH',
  DATA_SUTRAM: 'DATA_SUTRAM'
} as const;

export type AddressFilterType = 'LONGHORN' | 'CUSTOMER_OUTREACH';

export interface ActiveFilters {
  LONGHORN: boolean;
  CUSTOMER_OUTREACH: boolean;
}

export const INITIAL_FILTERS: ActiveFilters = {
  LONGHORN: false,
  CUSTOMER_OUTREACH: false
};
